import { http } from "@/utils/http"

const state = {
    token: "",
    user: null,
    authInitialized: false,
    tenant: null, // Stores current tenant context or logged in tenant
}

const getters = {
    authenticated: (state) => {
        return state.token && state.user ? true : false
    },
    tenantSubdomain: (state) => {
        return state.tenant ? state.tenant.subdomain : null
    }
}

const mutations = {
    SET_TOKEN: (state, token) => {
        state.token = token
        localStorage.setItem("AUTH_TOKEN", token)
    },

    SET_USER: (state, data) => {
        state.user = data
    },
    SET_AUTH_INITIALIZED: (state, data) => {
        state.authInitialized = data
    },
    SET_TENANT: (state, tenant) => {
        state.tenant = tenant
        // Persist subdomain in BOTH storages for maximum reliability:
        //   localStorage  → survives across tabs & browser restarts
        //   sessionStorage → per-tab, survives page refresh
        if (tenant && tenant.subdomain) {
            localStorage.setItem("TENANT_SUBDOMAIN", tenant.subdomain)
            sessionStorage.setItem("TENANT_SUBDOMAIN", tenant.subdomain)
        } else {
            localStorage.removeItem("TENANT_SUBDOMAIN")
            sessionStorage.removeItem("TENANT_SUBDOMAIN")
        }
    },
}

const actions = {
    getMe: async ({ commit }) => {
        let response = await http.get("/auth/me")
        const result = response.data.result
        commit("SET_USER", result)
    },
    login: async ({ commit, state }, formData) => {
        // If subdomain was passed from login form, set tenant BEFORE API call
        // so the X-Tenant-Subdomain header is included in the login request
        if (formData.subdomain && (!state.tenant || !state.tenant.subdomain)) {
            commit("SET_TENANT", { subdomain: formData.subdomain })
        }

        // Pass full formData to backend — backend may need email, password,
        // and subdomain in the body for tenant-scoped authentication
        let response = await http.post("/auth/login", formData)
        const result = response.data.result

        commit("SET_TOKEN", result.token)
        commit("SET_USER", result.user)

        // Set tenant from backend response, user data, or formData — in priority order
        // (Skip URL-based detection to avoid picking wrong tenant from production domains)
        if (result.tenant) {
            commit("SET_TENANT", result.tenant)
        } else {
            let detectedSub = null

            // Try user object
            if (result.user && result.user.subdomain) {
                detectedSub = result.user.subdomain
            }

            // Try formData subdomain (entered by user on login page)
            if (!detectedSub && formData.subdomain) {
                detectedSub = formData.subdomain
            }

            if (detectedSub) {
                commit("SET_TENANT", { subdomain: detectedSub })
            }
        }

        return
    },
    sendResetLink: async ({ commit }, formData) => {
        let response = await http.post("/auth/send-otp", formData)
        const result = response.data.result
        return
    },
    resetPassword: async ({ commit }, formData) => {
        let response = await http.post("/auth/verify-otp", formData)
        const result = response.data.result
        return
    },
    initAuth: async ({ commit }) => {
        // Restore tenant subdomain — check multiple sources for reliability
        let savedSubdomain =
            localStorage.getItem("TENANT_SUBDOMAIN") ||
            sessionStorage.getItem("TENANT_SUBDOMAIN")

        // For localhost development, try to detect from URL
        if (!savedSubdomain) {
            try {
                const hostname = window.location.hostname
                const parts = hostname.split(".")
                // Only detect from localhost patterns, not production domains
                if (parts.length >= 2 && parts[parts.length - 1] === "localhost") {
                    const sub = parts[0]
                    if (sub && sub !== "www" && sub !== "localhost") {
                        savedSubdomain = sub
                    }
                }
            } catch (_) { /* ignore */ }
        }

        if (savedSubdomain) {
            commit("SET_TENANT", { subdomain: savedSubdomain })
        }

        const token = localStorage.getItem("AUTH_TOKEN")
        let result
        if (token) {
            try {
                let response = await http.get("/auth/me")
                result = response.data.result

                commit("SET_TOKEN", token)
                commit("SET_USER", result)

                // Update tenant from backend response (most authoritative)
                if (result.tenant) {
                    commit("SET_TENANT", result.tenant)
                } else if (result.subdomain) {
                    commit("SET_TENANT", { subdomain: result.subdomain })
                }
            } catch (error) {
                console.log("caught error login", error)
            }
        }
        commit("SET_AUTH_INITIALIZED", true)
        return result
    },
    logout: async ({ commit }) => {
        commit("SET_TOKEN", "")
        commit("SET_USER", null)
        commit("SET_TENANT", null) // This clears both localStorage & sessionStorage via SET_TENANT
        localStorage.clear()
        sessionStorage.clear()
        // we can reload or redirect here if needed
    },
    detectTenant: ({ commit }) => {
        // Detect tenant from URL subdomain only for localhost (local development)
        // Skip production domains to avoid picking wrong tenant
        const hostname = window.location.hostname
        const parts = hostname.split(".")
        if (parts.length >= 2 && parts[parts.length - 1] === "localhost") {
            const sub = parts[0]
            if (sub && sub !== "www" && sub !== "localhost") {
                commit("SET_TENANT", { subdomain: sub })
                return sub
            }
        }
        return null
    },
    fetchTenant: async ({ commit }, subdomain) => {
        // Fetch tenant details from a public API endpoint
        // Assuming backend has GET /api/tenants/public/:subdomain or similar
        let response = await http.get(`/meta/tenant/${subdomain}`)
        const result = response.data.result
        commit("SET_TENANT", result)
        return result
    },
}

export default {
    namespaced: true,

    state,
    getters,
    actions,
    mutations,
}
